/**
 * Getting audio out of PadLab, and picking up audio the OS shared into it.
 *
 * Web Share is checked by *asking about the actual payload*, never by feature-sniffing
 * `navigator.share`. `canShare({ files })` is the only call that knows whether this browser,
 * on this platform, will accept a WAV — Safari on iOS has `navigator.share` and refuses file
 * payloads in several configurations, and a UI that offers "Share" and then throws is worse
 * than one that offers "Save to Files".
 *
 * Every path ends somewhere real: the share sheet, a download with a correct filename, or
 * the clipboard. There is no branch that reports success without moving bytes.
 */

import { createLogger, idPrefix, shape } from '../core/log';

const log = createLogger('share');

// MARK: - Filenames

const EXTENSION_FOR_TYPE: Readonly<Record<string, string>> = {
  'audio/wav': 'wav',
  'audio/x-wav': 'wav',
  'audio/wave': 'wav',
  'audio/vnd.wave': 'wav',
  'audio/aiff': 'aiff',
  'audio/x-aiff': 'aiff',
  'audio/mpeg': 'mp3',
  'audio/mp3': 'mp3',
  'audio/mp4': 'm4a',
  'audio/x-m4a': 'm4a',
  'audio/aac': 'aac',
  'audio/ogg': 'ogg',
  'audio/opus': 'opus',
  'audio/webm': 'webm',
  'audio/flac': 'flac',
  'audio/x-flac': 'flac',
  'audio/x-caf': 'caf',
  'application/x-padlab-project': 'padlab',
  'application/zip': 'zip',
  'application/json': 'json',
};

/**
 * Characters that break a filename on at least one of macOS, iOS, Windows or the web:
 * the C0 control range, DEL, and the reserved punctuation. Written as escapes because a
 * literal control character in source is invisible in every diff and every review.
 */
const ILLEGAL_FILENAME = /[\u0000-\u001f\u007f<>:"\/\\|?*]/g;

/**
 * Turns a project or take name into a filename a person would recognise in the Files app.
 *
 * The extension comes from the MIME type of the bytes we are about to write, not from
 * whatever the source was called — exporting "Vox take.mp3" as AAC and leaving the `.mp3`
 * on it produces a file that half the world refuses to open.
 */
export function suggestFilename(base: string, mimeType: string): string {
  const extension = EXTENSION_FOR_TYPE[mimeType.split(';')[0]?.trim().toLowerCase() ?? ''];

  let stem = base
    .replace(ILLEGAL_FILENAME, ' ')
    .replace(/\s+/g, ' ')
    .replace(/^[\s.]+|[\s.]+$/g, '');
  if (stem === '') stem = 'PadLab export';
  // 80 characters keeps the whole name visible in the Files app and well inside every
  // filesystem's per-component limit once an extension is added.
  if (stem.length > 80) stem = stem.slice(0, 80).trimEnd();

  if (extension === undefined) return stem;
  const lower = stem.toLowerCase();
  if (lower.endsWith(`.${extension}`)) return stem;
  return `${stem}.${extension}`;
}

// MARK: - Capability

function canShareData(data: ShareData): boolean {
  const canShare = (navigator as Navigator & { canShare?: (data?: ShareData) => boolean }).canShare;
  if (typeof navigator.share !== 'function') return false;
  if (typeof canShare !== 'function') {
    // Web Share level 1: text and URLs only, and no way to ask about anything else.
    return data.files === undefined || data.files.length === 0;
  }
  try {
    return canShare.call(navigator, data);
  } catch {
    return false;
  }
}

/**
 * Whether the share sheet will take these exact files. Pass the real files — a browser can
 * accept a `.txt` and refuse a 40 MB `.wav`.
 */
export function canShareFiles(files: readonly File[]): boolean {
  if (files.length === 0) return false;
  return canShareData({ files: [...files] });
}

export function canShareLink(): boolean {
  return canShareData({ url: window.location.href });
}

// MARK: - Sharing

export type ShareOutcome = 'shared' | 'downloaded' | 'copied' | 'cancelled' | 'failed';

export interface ShareRequest {
  readonly title?: string;
  readonly text?: string;
  readonly url?: string;
  readonly files?: readonly File[];
}

export interface ShareResult {
  readonly outcome: ShareOutcome;
  /** A sentence for the user: what happened, and what to do if it did not work. */
  readonly message: string;
}

/**
 * Shares the payload the best way this device allows.
 *
 * Must be called synchronously from a user gesture — `navigator.share` requires transient
 * activation, and on iOS an `await` before the call is enough to lose it.
 */
export async function shareContent(request: ShareRequest): Promise<ShareResult> {
  const files = request.files ?? [];

  if (files.length > 0) {
    // iOS rejects a payload that mixes files with a url in several versions, so the url is
    // only offered when this browser says it will take the combination.
    const withUrl: ShareData = {
      files: [...files],
      ...(request.title === undefined ? {} : { title: request.title }),
      ...(request.text === undefined ? {} : { text: request.text }),
      ...(request.url === undefined ? {} : { url: request.url }),
    };
    const withoutUrl: ShareData = { ...withUrl };
    delete withoutUrl.url;

    const payload = canShareData(withUrl) ? withUrl : canShareData(withoutUrl) ? withoutUrl : null;

    if (payload !== null) {
      try {
        await navigator.share(payload);
        return { outcome: 'shared', message: 'Shared.' };
      } catch (error) {
        if (isAbort(error)) return { outcome: 'cancelled', message: 'Sharing cancelled.' };
        log.warn('Share sheet refused a file payload, falling back to download', {
          files: files.length,
          cause: shape(error),
        });
      }
    }

    return downloadAll(files);
  }

  const linkPayload: ShareData = {
    ...(request.title === undefined ? {} : { title: request.title }),
    ...(request.text === undefined ? {} : { text: request.text }),
    ...(request.url === undefined ? {} : { url: request.url }),
  };

  if (canShareData(linkPayload)) {
    try {
      await navigator.share(linkPayload);
      return { outcome: 'shared', message: 'Shared.' };
    } catch (error) {
      if (isAbort(error)) return { outcome: 'cancelled', message: 'Sharing cancelled.' };
      log.warn('Share sheet refused a link payload', { cause: shape(error) });
    }
  }

  const text = request.url ?? request.text;
  if (text === undefined) {
    return { outcome: 'failed', message: 'There was nothing to share.' };
  }
  const copied = await copyText(text);
  return copied
    ? { outcome: 'copied', message: 'Link copied to the clipboard.' }
    : {
        outcome: 'failed',
        message: 'This browser would not share or copy automatically. Select the link and copy it.',
      };
}

function isAbort(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

// MARK: - Download fallback

/**
 * Writes a blob to the user's downloads with a specific filename.
 *
 * On iOS this lands in Files (or offers "Save to Files" from the download banner), which is
 * exactly where an exported stem needs to go. The object URL is revoked on a timer rather
 * than immediately: WebKit reads the blob asynchronously after the click, and revoking in
 * the same task produces a zero-byte file.
 */
export function downloadFile(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = 'noopener';
  anchor.style.setProperty('display', 'none');
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

function downloadAll(files: readonly File[]): ShareResult {
  for (const file of files) downloadFile(file, file.name);
  return {
    outcome: 'downloaded',
    message:
      files.length === 1
        ? `Saved “${files[0]!.name}”. Choose Save to Files to keep it.`
        : `Saved ${files.length} files. Choose Save to Files to keep them.`,
  };
}

// MARK: - Clipboard

/**
 * Copies text, with a fallback for browsers that gate the async clipboard behind a
 * permission PadLab has no reason to ask for. `execCommand` is deprecated and still the only
 * thing that works in a non-secure context or an older WebKit, so it stays as the second
 * attempt rather than the first.
 */
export async function copyText(text: string): Promise<boolean> {
  if (typeof navigator.clipboard?.writeText === 'function') {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (error) {
      log.warn('Async clipboard write failed, trying the selection fallback', {
        cause: shape(error),
      });
    }
  }

  const field = document.createElement('textarea');
  field.value = text;
  field.setAttribute('readonly', '');
  field.setAttribute('aria-hidden', 'true');
  // Positioned off-screen rather than hidden: a display:none element cannot be selected.
  field.style.setProperty('position', 'fixed');
  field.style.setProperty('top', '0');
  field.style.setProperty('left', '-9999px');
  field.style.setProperty('opacity', '0');
  document.body.appendChild(field);

  try {
    field.focus({ preventScroll: true });
    field.select();
    // iOS ignores select() on a readonly field; the explicit range is what makes it work.
    field.setSelectionRange(0, text.length);
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    field.remove();
  }
}

// MARK: - Incoming shares (Web Share Target)
//
// The service worker receives the OS share POST, stores the payload in a cache and redirects
// to `?share=<token>`. Reading it back is the page's job, and it happens exactly once: the
// entries are deleted as they are read so a reload cannot import the same clip twice.
//
// These two constants mirror INBOX_CACHE and INBOX_PATH in src/sw.ts. The worker is compiled
// as a separate self-contained script and cannot share a module with the app.

const INBOX_CACHE = 'padlab-inbox';
const INBOX_PATH = 'share-inbox/';

export interface SharedPayload {
  readonly token: string;
  readonly receivedAt: number;
  readonly title: string;
  readonly text: string;
  readonly url: string;
  readonly files: readonly File[];
  /** Files the worker refused — too large, or empty. Named so the user can be told. */
  readonly rejected: readonly string[];
}

interface InboxFileRecord {
  readonly index: number;
  readonly name: string;
  readonly type: string;
  readonly size: number;
}

interface InboxRecord {
  readonly token: string;
  readonly receivedAt: number;
  readonly title: string;
  readonly text: string;
  readonly url: string;
  readonly files: readonly InboxFileRecord[];
  readonly rejected: readonly string[];
}

async function inboxBase(): Promise<URL> {
  const registration = await navigator.serviceWorker?.getRegistration().catch(() => undefined);
  // The registration scope is authoritative; `./` is only correct while the page sits at the
  // scope root, which stops being true as soon as the router pushes a path.
  return new URL(registration?.scope ?? new URL('./', window.location.href).href);
}

/**
 * Collects the files the OS shared into PadLab, then removes them from the inbox.
 *
 * Returns `null` when the token is unknown — which is the normal outcome of reloading a page
 * whose `?share=` parameter has already been consumed, not an error.
 */
export async function takeSharedPayload(token: string): Promise<SharedPayload | null> {
  if (typeof caches === 'undefined') return null;
  if (!/^[0-9a-f]{8,64}$/.test(token)) return null;

  const hasCache = await caches.has(INBOX_CACHE).catch(() => false);
  if (!hasCache) return null;

  const cache = await caches.open(INBOX_CACHE);
  const base = await inboxBase();
  const metaURL = new URL(`${INBOX_PATH}${token}/meta`, base).href;

  const metaResponse = await cache.match(metaURL);
  if (metaResponse === undefined) return null;

  const record = (await metaResponse.json().catch(() => null)) as InboxRecord | null;
  if (record === null) {
    await cache.delete(metaURL);
    return null;
  }

  const files: File[] = [];
  for (const entry of record.files) {
    const fileURL = new URL(`${INBOX_PATH}${token}/file/${entry.index}`, base).href;
    const response = await cache.match(fileURL);
    if (response === undefined) continue;
    const blob = await response.blob();
    files.push(
      new File([blob], entry.name, {
        type: entry.type !== '' ? entry.type : blob.type,
        lastModified: record.receivedAt,
      }),
    );
    await cache.delete(fileURL);
  }
  await cache.delete(metaURL);

  log.info('Collected a shared payload', {
    token: idPrefix(token),
    files: files.length,
    rejected: record.rejected.length,
  });

  return {
    token,
    receivedAt: record.receivedAt,
    title: record.title,
    text: record.text,
    url: record.url,
    files,
    rejected: record.rejected,
  };
}

/**
 * The sentence to show when the share target ran but produced nothing usable. Web Share
 * Target is a Chromium feature; WebKit has never implemented it, so on iOS audio always
 * arrives through the file importer instead and this path is never reached.
 */
export function shareTargetFailureMessage(): string {
  return 'PadLab could not read what was shared. Try importing the file from the sound library instead.';
}

/** Used by the install screen and the "share this project" action alike. */
export function installationURL(): string {
  const url = new URL('./', window.location.href);
  url.hash = '';
  url.search = '';
  return url.href;
}
